import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import EditorModalPortal from './EditorModalPortal';
import BackgroundFields, { Field, inputClass } from './BackgroundFields';
import SiteFontStyles from './SiteFontStyles';
import SiteCustomFontsPanel from './SiteCustomFontsPanel';
import { DEFAULT_BACKGROUND, DEFAULT_SEO, DEFAULT_GEO, DEFAULT_TYPOGRAPHY, GEO_ENTITY_TYPES, migrateSite } from '../utils/defaults';
import { DEFAULT_AMBIENT_MUSIC, normalizeAmbientMusic } from '../utils/ambientMusic';
import { uploadWebCreatorSiteMedia, repairWebCreatorInlineAssets } from '../../../api/webCreator';
import { isInlineWebCreatorMedia, resolveWebCreatorMediaUrl } from '../utils/webCreatorMedia';
const TABS = [
  { id: 'general', label: 'Général', icon: 'FileText' },
  { id: 'appearance', label: 'Apparence', icon: 'Palette' },
  { id: 'seo', label: 'SEO', icon: 'Search' },
  { id: 'geo', label: 'GEO', icon: 'Bot' },
];

export default function SiteConfigModal({ site, open, onClose, onSave, onSiteRepaired }) {
  const [tab, setTab] = useState('general');
  const [draft, setDraft] = useState(null);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaError, setMediaError] = useState('');
  const [repairing, setRepairing] = useState(false);
  const siteId = site?._id || site?.id;

  useEffect(() => {
    if (site && open) {
      setDraft({
        title: site.title || '',
        slug: site.slug || '',
        description: site.description || '',
        templateDescription: site.templateDescription || '',
        pageBackground: site.pageBackground || { ...DEFAULT_BACKGROUND },
        seo: { ...DEFAULT_SEO, ...(site.seo || {}) },
        geo: { ...DEFAULT_GEO, ...(site.geo || {}) },
        typography: { ...DEFAULT_TYPOGRAPHY, ...(site.typography || {}) },
        ambientMusic: normalizeAmbientMusic(site.ambientMusic || DEFAULT_AMBIENT_MUSIC),
      });
      setTab('general');
    }
  }, [site, open]);

  if (!open || !site || !draft) return null;

  const setSeo = (key, value) => {
    setDraft((prev) => ({
      ...prev,
      seo: { ...prev.seo, [key]: value },
    }));
  };

  const setGeo = (key, value) => {
    setDraft((prev) => ({
      ...prev,
      geo: { ...prev.geo, [key]: value },
    }));
  };

  const handleShareImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setSeo('shareImageUrl', reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <EditorModalPortal open={open}>
      <SiteFontStyles typography={draft.typography} />
      <div className="w-full max-w-2xl rounded-xl bg-card border border-border shadow-elevation-3 overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between border-b border-border px-4 py-3 shrink-0">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Icon name="Settings" size={18} />
            {site.isTemplate ? 'Configuration du template' : 'Configuration du site'}
          </h3>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-muted">
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="flex border-b border-border shrink-0">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-smooth ${
                tab === t.id
                  ? 'text-primary border-b-2 border-primary bg-primary/5'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
              }`}
              onClick={() => setTab(t.id)}
            >
              <Icon name={t.icon} size={14} />
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {tab === 'general' && (
            <>
              <Field label="Nom du site">
                <input
                  className={inputClass}
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="Mon site"
                />
              </Field>
              <Field label="Description">
                <textarea
                  className={`${inputClass} min-h-[88px]`}
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder={
                    site.isTemplate
                      ? 'Description interne du template…'
                      : 'Courte description de votre page…'
                  }
                />
              </Field>
              {site.isTemplate ? (
                <Field label="Description affichée dans la galerie">
                  <textarea
                    className={`${inputClass} min-h-[88px]`}
                    value={draft.templateDescription}
                    onChange={(e) =>
                      setDraft({ ...draft, templateDescription: e.target.value })
                    }
                    placeholder="Texte visible par les utilisateurs dans l’onglet Templates…"
                  />
                </Field>
              ) : null}
              <Field label={site.isTemplate ? 'Identifiant du template' : 'Identifiant de la page'}>
                <input
                  className={inputClass}
                  value={draft.slug}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                    })
                  }
                  placeholder={site.isTemplate ? 'tpl-mon-template' : 'mon-site'}
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  {site.isTemplate
                    ? 'Doit commencer par tpl-. Lettres minuscules, chiffres et tirets.'
                    : 'Lettres minuscules, chiffres et tirets. Utilisez « Copier le lien » pour partager l’adresse complète.'}
                </p>
              </Field>
            </>
          )}

          {tab === 'appearance' && (
            <>
              <p className="text-xs text-muted-foreground">
                Le fond s&apos;applique derrière toutes les sections de la page.
              </p>
              <BackgroundFields
                label="Fond d&apos;écran de la page"
                value={draft.pageBackground}
                onChange={(pageBackground) => setDraft({ ...draft, pageBackground })}
              />

              <SiteCustomFontsPanel
                typography={draft.typography}
                onChange={(typography) => setDraft((prev) => ({ ...prev, typography }))}
              />

              <div className="rounded-lg border border-border p-3 space-y-3">
                <p className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Icon name="Music" size={16} />
                  Musique d&apos;ambiance
                </p>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={draft.ambientMusic?.enabled === true}
                    onChange={(e) =>
                      setDraft((prev) => ({
                        ...prev,
                        ambientMusic: {
                          ...prev.ambientMusic,
                          enabled: e.target.checked,
                        },
                      }))
                    }
                  />
                  <span className="text-xs text-muted-foreground">
                    Lire une piste MP3 en boucle sur la page publiée (les visiteurs peuvent couper le son
                    depuis le menu).
                  </span>
                </label>
                <Field label="URL du fichier MP3 (recommandé)">
                  <input
                    className={inputClass}
                    value={
                      draft.ambientMusic?.audioUrl?.startsWith('data:')
                        ? ''
                        : draft.ambientMusic?.audioUrl || ''
                    }
                    onChange={(e) =>
                      setDraft((prev) => ({
                        ...prev,
                        ambientMusic: {
                          ...prev.ambientMusic,
                          audioUrl: e.target.value.trim(),
                          enabled: e.target.value.trim() ? true : prev.ambientMusic?.enabled,
                        },
                      }))
                    }
                    placeholder="https://…/ambiance.mp3"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Lien direct vers un MP3 — plus fiable qu&apos;un import lourd dans la base.
                  </p>
                </Field>
                <Field label="Ou importer un MP3">
                  <input
                    type="file"
                    accept="audio/mpeg,audio/mp3,.mp3"
                    disabled={!siteId || mediaUploading}
                    className="block w-full text-xs text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-border file:bg-muted file:text-foreground"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !siteId) return;
                      setMediaError('');
                      setMediaUploading(true);
                      try {
                        const res = await uploadWebCreatorSiteMedia(siteId, file);
                        const url = res?.data?.url;
                        if (!url) throw new Error('Réponse serveur invalide.');
                        setDraft((prev) => ({
                          ...prev,
                          ambientMusic: {
                            ...prev.ambientMusic,
                            audioUrl: url,
                            enabled: true,
                          },
                        }));
                      } catch (err) {
                        setMediaError(err.message || 'Envoi du MP3 impossible.');
                      } finally {
                        setMediaUploading(false);
                        e.target.value = '';
                      }
                    }}
                  />
                  {mediaUploading ? (
                    <p className="text-[11px] text-muted-foreground mt-1">Envoi du fichier…</p>
                  ) : null}
                  {mediaError ? (
                    <p className="text-[11px] text-destructive mt-1">{mediaError}</p>
                  ) : null}
                  {isInlineWebCreatorMedia(draft.ambientMusic?.audioUrl) ? (
                    <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1">
                      Ce MP3 est encore embarqué dans la base (trop lourd). Cliquez « Réparer les médias »
                      ci-dessous ou réimportez le fichier.
                    </p>
                  ) : null}
                  {draft.ambientMusic?.audioUrl && !isInlineWebCreatorMedia(draft.ambientMusic.audioUrl) ? (
                    <audio
                      className="w-full mt-2 h-8"
                      controls
                      src={resolveWebCreatorMediaUrl(draft.ambientMusic.audioUrl)}
                      preload="metadata"
                    />
                  ) : (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Le fichier est enregistré sur le serveur (dossier uploads), pas dans MongoDB.
                    </p>
                  )}
                </Field>
                {siteId ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={repairing || mediaUploading}
                    onClick={async () => {
                      setRepairing(true);
                      setMediaError('');
                      try {
                        const res = await repairWebCreatorInlineAssets(siteId);
                        const next = migrateSite(res?.data?.site);
                        if (next) {
                          onSiteRepaired?.(next);
                          setDraft((prev) => ({
                            ...prev,
                            ambientMusic: normalizeAmbientMusic(
                              next.ambientMusic || prev.ambientMusic,
                            ),
                          }));
                        }
                      } catch (err) {
                        setMediaError(err.message || 'Réparation impossible.');
                      } finally {
                        setRepairing(false);
                      }
                    }}
                  >
                    {repairing ? 'Réparation…' : 'Réparer les médias embarqués'}
                  </Button>
                ) : null}
                <Field
                  label={`Volume — ${Math.round((draft.ambientMusic?.volume ?? 0.35) * 100)} %`}
                >
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    className="w-full"
                    value={Math.round((draft.ambientMusic?.volume ?? 0.35) * 100)}
                    disabled={!draft.ambientMusic?.enabled}
                    onChange={(e) => {
                      const pct = Number(e.target.value);
                      setDraft((prev) => ({
                        ...prev,
                        ambientMusic: {
                          ...prev.ambientMusic,
                          volume: Math.min(1, Math.max(0, pct / 100)),
                        },
                      }));
                    }}
                  />
                </Field>
              </div>
            </>
          )}

          {tab === 'geo' && (
            <>
              <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5">
                <p className="text-xs text-foreground leading-relaxed">
                  <strong className="font-semibold">GEO</strong> (Generative Engine Optimization) — optimise
                  votre page pour être comprise et citée par les moteurs IA (ChatGPT, Perplexity, Claude…).
                </p>
              </div>

              <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-border p-3">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={draft.geo.enabled !== false}
                  onChange={(e) => setGeo('enabled', e.target.checked)}
                />
                <span>
                  <span className="text-sm font-medium text-foreground block">
                    Autoriser l&apos;indexation IA
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Les crawlers IA pourront lire et référencer votre page dans leurs réponses.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-border p-3">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={draft.geo.allowTraining === true}
                  onChange={(e) => setGeo('allowTraining', e.target.checked)}
                  disabled={draft.geo.enabled === false}
                />
                <span>
                  <span className="text-sm font-medium text-foreground block">
                    Autoriser l&apos;entraînement IA
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Permet l&apos;utilisation de votre contenu pour entraîner des modèles (GPTBot, CCBot…).
                    Désactivé par défaut.
                  </span>
                </span>
              </label>

              <Field label="Résumé pour les IA">
                <textarea
                  className={`${inputClass} min-h-[88px]`}
                  value={draft.geo.summary || ''}
                  onChange={(e) => setGeo('summary', e.target.value)}
                  disabled={draft.geo.enabled === false}
                  placeholder="Décrivez en 2-3 phrases ce que fait votre page, pour qui, et quelle valeur elle apporte. Les IA s'en serviront pour vous citer correctement."
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Soyez factuel et concis — évitez le marketing creux.
                </p>
              </Field>

              <Field label="Sujets / mots-clés">
                <input
                  className={inputClass}
                  value={draft.geo.topics || ''}
                  onChange={(e) => setGeo('topics', e.target.value)}
                  disabled={draft.geo.enabled === false}
                  placeholder="notaire, immobilier, succession, Lyon"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Séparez par des virgules. Aide les IA à classer votre page.
                </p>
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Nom de l'entité">
                  <input
                    className={inputClass}
                    value={draft.geo.entityName || ''}
                    onChange={(e) => setGeo('entityName', e.target.value)}
                    disabled={draft.geo.enabled === false}
                    placeholder={draft.title || 'Cabinet Duval'}
                  />
                </Field>
                <Field label="Type d'entité">
                  <select
                    className={inputClass}
                    value={draft.geo.entityType || 'organization'}
                    onChange={(e) => setGeo('entityType', e.target.value)}
                    disabled={draft.geo.enabled === false}
                  >
                    {GEO_ENTITY_TYPES.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Public cible">
                <input
                  className={inputClass}
                  value={draft.geo.audience || ''}
                  onChange={(e) => setGeo('audience', e.target.value)}
                  disabled={draft.geo.enabled === false}
                  placeholder="Particuliers, PME, professionnels du droit…"
                />
              </Field>

              <Field label="Faits clés">
                <textarea
                  className={`${inputClass} min-h-[100px] font-mono text-sm`}
                  value={draft.geo.keyFacts || ''}
                  onChange={(e) => setGeo('keyFacts', e.target.value)}
                  disabled={draft.geo.enabled === false}
                  placeholder={'Fondé en 1987 à Lyon\n6 notaires associés\n+4500 dossiers traités\nSpécialité : droit immobilier'}
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Un fait par ligne. Les IA les utiliseront pour des réponses précises.
                </p>
              </Field>

              <Field label="Langue du contenu">
                <select
                  className={inputClass}
                  value={draft.geo.language || 'fr'}
                  onChange={(e) => setGeo('language', e.target.value)}
                  disabled={draft.geo.enabled === false}
                >
                  <option value="fr">Français</option>
                  <option value="en">Anglais</option>
                  <option value="es">Espagnol</option>
                  <option value="de">Allemand</option>
                  <option value="it">Italien</option>
                </select>
              </Field>
            </>
          )}

          {tab === 'seo' && (
            <>
              <p className="text-xs text-muted-foreground">
                Ces informations sont utilisées quand votre page est partagée sur les réseaux
                sociaux ou dans les résultats de recherche.
              </p>
              <Field label="Titre affiché au partage">
                <input
                  className={inputClass}
                  value={draft.seo.shareTitle || ''}
                  onChange={(e) => setSeo('shareTitle', e.target.value)}
                  placeholder={draft.title || 'Titre du site'}
                />
              </Field>
              <Field label="Description au partage">
                <textarea
                  className={`${inputClass} min-h-[80px]`}
                  value={draft.seo.shareDescription || ''}
                  onChange={(e) => setSeo('shareDescription', e.target.value)}
                  placeholder={draft.description || 'Description de la page…'}
                />
              </Field>
              <Field label="Image de partage (URL)">
                <input
                  className={inputClass}
                  value={draft.seo.shareImageUrl || ''}
                  onChange={(e) => setSeo('shareImageUrl', e.target.value)}
                  placeholder="https://…"
                />
              </Field>
              <Field label="Importer une image de partage">
                <input type="file" accept="image/*" onChange={handleShareImageFile} className="text-sm" />
              </Field>
              {draft.seo.shareImageUrl ? (
                <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
                  <img
                    src={draft.seo.shareImageUrl}
                    alt=""
                    className="w-full max-h-36 object-cover"
                  />
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-4 py-3 shrink-0">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={() => {
              const am = draft.ambientMusic || {};
              const hasTrack = Boolean(String(am.audioUrl || '').trim());
              onSave({
                ...draft,
                ambientMusic: {
                  ...am,
                  enabled: hasTrack ? true : am.enabled === true,
                },
              });
            }}
          >
            Enregistrer
          </Button>
        </div>
      </div>
    </EditorModalPortal>
  );
}
